import { Component, Input, Output, EventEmitter, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FikoYT3dSplineDialogComponent } from './fikoyt-3d-spline-dialog.component';
import { FikoYT3dSpline } from './fikoyt-3d-spline';
import { FikoSahne } from '../../FikoSahne';

/**
 * 3D Spline Toolbar Button Component
 *
 * Arac cubuguna eklenebilen 3D spline olusturma butonu.
 * Tiklandiginda spline olusturma dialogunu acar.
 *
 * Kullanim:
 * ```html
 * <fikoyt-3d-spline-toolbar-button
 *   [sahne]="fikoSahne"
 *   (splineOlusturuldu)="handleSpline($event)">
 * </fikoyt-3d-spline-toolbar-button>
 * ```
 */
@Component({
    selector: 'fikoyt-3d-spline-toolbar-button',
    template: `
        <!-- Toolbar Button -->
        <button class="fiko-3d-spline-btn"
                [class.active]="dialogAcik"
                (click)="dialogAcKapa()"
                title="3D Spline Olustur (Catmull-Rom / Bezier)">
            <!-- SVG Icon - Spline Curve -->
            <svg viewBox="0 0 24 24"
                 width="18"
                 height="18"
                 fill="none"
                 stroke="currentColor"
                 stroke-width="2"
                 stroke-linecap="round"
                 stroke-linejoin="round">
                <!-- Smooth spline curve -->
                <path d="M2,18 C6,18 4,6 10,6 S14,18 18,18 S20,10 22,6"/>
                <!-- Control points -->
                <circle cx="2" cy="18" r="2" fill="currentColor" stroke="none"/>
                <circle cx="10" cy="6" r="2" fill="currentColor" stroke="none"/>
                <circle cx="18" cy="18" r="2" fill="currentColor" stroke="none"/>
                <circle cx="22" cy="6" r="2" fill="currentColor" stroke="none"/>
            </svg>
        </button>

        <!-- Dialog Component -->
        <fikoyt-3d-spline-dialog
            #splineDialog
            [sahne]="sahne"
            [gorunur]="dialogAcik"
            (splineOlusturuldu)="splineOlusturulduHandler($event)"
            (kapandi)="dialogKapandiHandler()">
        </fikoyt-3d-spline-dialog>
    `,
    styles: [`
        :host {
            display: inline-block;
        }

        .fiko-3d-spline-btn {
            width: 28px;
            height: 28px;
            border: 1px solid rgba(255, 165, 0, 0.3);
            border-radius: 6px;
            background: linear-gradient(135deg, rgba(80, 50, 0, 0.8), rgba(50, 30, 0, 0.9));
            color: rgba(255, 165, 0, 0.9);
            cursor: pointer;
            display: flex;
            align-items: center;
            justify-content: center;
            transition: all 0.2s ease;
            padding: 0;

            &:hover {
                background: linear-gradient(135deg, rgba(100, 70, 0, 0.9), rgba(70, 45, 0, 0.95));
                border-color: rgba(255, 165, 0, 0.6);
                box-shadow: 0 0 12px rgba(255, 165, 0, 0.4);
                transform: scale(1.05);

                svg {
                    filter: drop-shadow(0 0 4px rgba(255, 165, 0, 0.6));
                }
            }

            &:active {
                transform: scale(0.98);
            }

            &.active {
                background: linear-gradient(135deg, rgba(140, 100, 0, 0.95), rgba(100, 70, 0, 0.95));
                border-color: rgba(255, 165, 0, 0.8);
                box-shadow: 0 0 15px rgba(255, 165, 0, 0.5);
                color: #ffa500;

                svg {
                    filter: drop-shadow(0 0 6px rgba(255, 165, 0, 0.8));
                }
            }

            svg {
                transition: filter 0.2s ease;
            }
        }
    `],
    standalone: true,
    imports: [CommonModule, FikoYT3dSplineDialogComponent]
})
export class FikoYT3dSplineToolbarButtonComponent {

    /** Sahne referansi */
    @Input() sahne?: FikoSahne;

    /** Spline olusturuldugunda emit */
    @Output() splineOlusturuldu = new EventEmitter<FikoYT3dSpline>();

    /** Dialog referansi */
    @ViewChild('splineDialog') splineDialog?: FikoYT3dSplineDialogComponent;

    /** Dialog acik mi? */
    dialogAcik: boolean = false;

    /**
     * Dialog ac/kapa toggle
     */
    dialogAcKapa(): void {
        this.dialogAcik = !this.dialogAcik;
        if (this.dialogAcik && this.splineDialog) {
            this.splineDialog.ac();
        }
    }

    /**
     * Spline olusturuldugunda
     */
    splineOlusturulduHandler(spline: FikoYT3dSpline): void {
        this.splineOlusturuldu.emit(spline);
        this.dialogAcik = false;

        // Sahneye ekle (zaten dialog icinde ekleniyor ama emin olmak icin)
        if (this.sahne && spline.parent !== this.sahne) {
            this.sahne.add(spline);
        }
    }

    /**
     * Dialog kapandiginda
     */
    dialogKapandiHandler(): void {
        this.dialogAcik = false;
    }
}

/**
 * 3D Spline SVG Icon (standalone kullanim icin)
 */
export const SPLINE_3D_ICON_SVG = `
<svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
    <path d="M2,18 C6,18 4,6 10,6 S14,18 18,18 S20,10 22,6"/>
    <circle cx="2" cy="18" r="2" fill="currentColor" stroke="none"/>
    <circle cx="10" cy="6" r="2" fill="currentColor" stroke="none"/>
    <circle cx="18" cy="18" r="2" fill="currentColor" stroke="none"/>
    <circle cx="22" cy="6" r="2" fill="currentColor" stroke="none"/>
</svg>
`;
